import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import './TotalReviewProgressWidget.scss';
import ArrWidgetWrapper from '../../../common/arr-widget-wrapper/ArrWidgetWrapper';
import {t} from 'i18next';
import {createPortal} from 'react-dom';

interface Stage {
  stageName: string;
  completedPercentage: number;
  distributedPercentage: number;
  notDistributedPercentage: number;
}

interface TotalReviewProgressProps {
  overallCompletedPercentage: number;
  overallDistributedPercentage: number;
  overallNotDistributedPercentage: number;
  stageProgress: Stage[];
  onReplaceClick?: () => void;
  loading: boolean;
  error: boolean;
}

const TOOLTIP_Y_POSITION = 76;

type ProgressProps = {
  sections: {
    percent: number;
    colorClass: string;
    testId?: string;
    label: string;
  }[];
  testId?: string;
};

const ProgressChart = ({sections}: ProgressProps) => {
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState({visible: false, x: 0, y: 0});

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    setTooltip((prev) => ({...prev, x: e.clientX, y: rect.top}));
  };

  const showEmpty = sections.every((section) => section.percent === 0);
  return showEmpty ? (
    <Box className="empty-progress" data-testid="empty-bar" />
  ) : (
    <Box
      ref={barRef}
      className="progress-section"
      onMouseEnter={() => setTooltip((t) => ({...t, visible: true}))}
      onMouseLeave={() => setTooltip((t) => ({...t, visible: false}))}
      onMouseMove={handleMouseMove}
    >
      {tooltip.visible &&
        createPortal(
          <Box
            className="trp-tooltip-wrapper"
            sx={{
              top: tooltip.y - TOOLTIP_Y_POSITION,
              left: tooltip.x,
            }}
          >
            {sections.map((section, index) => (
              <Box key={section.testId}>
                <Typography
                  className={'typo-text label3_size black-text'}
                  data-testid={`total-review-progress-widget-section-label-${index}`}
                >
                  {section.label}: {section.percent.toFixed(2)}%
                </Typography>
              </Box>
            ))}
          </Box>,
          document.body
        )}
      {sections.map((section) => {
        return section.percent > 0 ? (
          <Box
            className="section"
            key={section.colorClass}
            sx={{flex: section.percent}}
            data-testid={`${section.testId}-container`}
          >
            <Box
              className={`bar ${section.colorClass}`}
              data-testid={`${section.testId}-container-color`}
            ></Box>
          </Box>
        ) : null;
      })}
    </Box>
  );
};

const TotalReviewProgressWidget: React.FC<TotalReviewProgressProps> = ({
  overallCompletedPercentage,
  overallDistributedPercentage,
  overallNotDistributedPercentage,
  stageProgress = [],
  onReplaceClick,
  loading,
  error,
}) => {
  const progressParameters = [
    {
      title: t('dashboard.totalReview.completed'),
      className: 'progress-completed',
    },
    {
      title: t('dashboard.totalReview.distributed'),
      className: 'progress-distributed',
    },
    {
      title: t('dashboard.totalReview.notDistributed'),
      className: 'progress-notDistributed',
    },
  ];
  return (
    <ArrWidgetWrapper
      title={t('dashboard.totalReview.title')}
      showInfo
      showReplace
      onReplaceClick={onReplaceClick}
      loading={loading}
      error={error}
      tooltipContent={t('dashboard.totalReview.tooltip')}
    >
      <Box className="total-progress-root">
        <Box gap={3} className="progress-labels">
          {progressParameters.map((param, idx) => (
            <Box key={param.title} gap={1} className={`progress-parameter`}>
              <Box className={`color-box ${param.className}`}></Box>
              <Typography
                className={'typo-text label4_size'}
                data-testid={`trp-${idx}-title`}
              >
                {param.title}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box className="overall-progress">
          <Typography
            className={'typo-text subhead5_size fit-content'}
            data-testid={`trp-overall`}
          >
            {t('dashboard.totalReview.overall')}
          </Typography>
          <ProgressChart
            sections={[
              {
                percent: overallCompletedPercentage,
                colorClass: 'progress-completed',
                testId: 'overall-complete',
                label: t('dashboard.totalReview.completed'),
              },
              {
                percent: overallDistributedPercentage,
                colorClass: 'progress-distributed leftBorder',
                testId: 'distributed-complete',
                label: t('dashboard.totalReview.distributed'),
              },
              {
                percent: overallNotDistributedPercentage,
                colorClass: 'progress-notDistributed leftBorder',
                testId: 'not-distributed-complete',
                label: t('dashboard.totalReview.notDistributed'),
              },
            ]}
          />
        </Box>

        <Box className="review-stage">
          <Typography
            className={'typo-text subhead5_size fit-content'}
            data-testid={`total-review-progress-widget-by-review-stage`}
          >
            {t('dashboard.totalReview.byReviewStage')}
          </Typography>
          {stageProgress.map((stage, index) => (
            <Box key={stage.stageName} className={'review-stage-text'}>
              <Typography
                className={'typo-text label4_size fit-content stage-text-color'}
                data-testid={`stage-name-${index}`}
              >
                {stage.stageName}
              </Typography>
              <ProgressChart
                sections={[
                  {
                    percent: stage.completedPercentage,
                    colorClass: 'progress-completed',
                    testId: `progress-completed-${index}`,
                    label: t('dashboard.totalReview.completed'),
                  },
                  {
                    percent: stage.distributedPercentage,
                    colorClass: 'progress-distributed leftBorder',
                    testId: `progress-distributed-${index}`,
                    label: t('dashboard.totalReview.distributed'),
                  },
                  {
                    percent: stage.notDistributedPercentage,
                    colorClass: 'progress-notDistributed leftBorder',
                    testId: `progress-notDistributed-${index}`,
                    label: t('dashboard.totalReview.notDistributed'),
                  },
                ]}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </ArrWidgetWrapper>
  );
};

export default TotalReviewProgressWidget;
